import React, { useEffect, useState } from 'react';
import './StudentListingCard.css';
import useRevealOnScroll from '../../hooks/useRevealOnScroll';
import { toggleFavoriteItem } from '../../api/favorites';
import { getAuthToken } from '../../utils/authStorage';
import { applyAvatarFallback, resolveAvatarSrc } from '../../utils/avatarPlaceholder';
import { useI18n } from '../../i18n/language';

// 统一时区城市显示（与时区下拉一致）
const getTimeZoneCityMap = (t) => ({
  '+13': t('listings.city.auckland', '奥克兰'),
  '+11': t('listings.city.solomon', '所罗门群岛'),
  '+10': t('listings.city.brisbane', '布里斯班'),
  '+9': t('listings.city.tokyo', '东京'),
  '+08': t('listings.city.shanghai', '上海'),
  '+8': t('listings.city.shanghai', '上海'),
  '+7': t('listings.city.bangkok', '曼谷'),
  '+6': t('listings.city.dhaka', '达卡'),
  '+5': t('listings.city.karachi', '卡拉奇'),
  '+4': t('listings.city.dubai', '迪拜'),
  '+3': t('listings.city.moscow', '莫斯科'),
  '+2': t('listings.city.johannesburg', '约翰内斯堡'),
  '+1': t('listings.city.berlin', '柏林'),
  '+0': t('listings.city.london', '伦敦'),
  '-8': t('listings.city.losAngeles', '洛杉矶'),
  '-7': t('listings.city.california', '加州'),
  '-6': t('listings.city.chicago', '芝加哥'),
  '-5': t('listings.city.newYork', '纽约'),
  '-4': t('listings.city.halifax', '哈利法克斯'),
  '-3': t('listings.city.saoPaulo', '圣保罗'),
});

const getTimeZoneOffsetMinutes = (timeZone, referenceDate = new Date()) => {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = Object.fromEntries(fmt.formatToParts(referenceDate).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
    const iso = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}.000Z`;
    const tzAsUTC = new Date(iso);
    return Math.round((tzAsUTC.getTime() - referenceDate.getTime()) / 60000);
  } catch {
    return 0;
  }
};

const buildUtcOffsetLabel = (offsetMinutes) => {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  if (minutes === 0) return `UTC${sign}${hours}`;
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatTimezoneWithCity = (tz, t) => {
  if (!tz) return '';
  const cityMap = getTimeZoneCityMap(t);
  const raw = String(tz).trim();
  const stripped = raw.replace(/\s*\(.*\)\s*$/, '').trim();
  const base = raw.includes('/') && !/UTC\s*[+-]/i.test(stripped)
    ? buildUtcOffsetLabel(getTimeZoneOffsetMinutes(raw))
    : stripped;

  const match = base.match(/UTC\s*([+-])\s*(\d{1,2})(?::\d{2})?/i);
  if (!match) return stripped || raw;
  const sign = match[1] === '-' ? '-' : '+';
  const hoursRaw = match[2];
  const hoursKey = hoursRaw.length === 1 ? `${sign}${hoursRaw}` : `${sign}${hoursRaw.padStart(2, '0')}`;
  const city = cityMap[hoursKey] || cityMap[`${sign}${hoursRaw}`];
  return city ? `${base} (${city})` : base;
};

function StudentListingCard({
  data,
  favoriteRole,
  favoriteItemType,
  favoriteItemId,
  initialFavorited = false,
  onFavoriteChange,
}) {
  const { getCourseDirectionDisplayLabel, t } = useI18n();
  const [isFavorited, setIsFavorited] = useState(!!initialFavorited);
  const { ref: revealRef, visible } = useRevealOnScroll();

  const getReturnPath = () => {
    try {
      const { pathname, search, hash } = window.location;
      const path = `${pathname}${search || ''}${hash || ''}`;
      return path || '/student';
    } catch {
      return '/student';
    }
  };

  useEffect(() => {
    setIsFavorited(!!initialFavorited);
  }, [initialFavorited]);

  const toggleFavorite = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.(); // 防止点击爱心时触发卡片跳转（a[target=_blank]）
    if (!favoriteRole || !favoriteItemType) {
      setIsFavorited((v) => !v);
      return;
    }

    const itemId = typeof favoriteItemId !== 'undefined' ? String(favoriteItemId) : (typeof data?.id !== 'undefined' ? String(data.id) : '');
    if (!itemId) {
      setIsFavorited((v) => !v);
      return;
    }

    const token = getAuthToken();

    if (!token) {
      try { window.dispatchEvent(new CustomEvent('auth:login-required', { detail: { from: getReturnPath() } })); } catch {}
      return;
    }

    try {
      const res = await toggleFavoriteItem({
        role: favoriteRole,
        itemType: favoriteItemType,
        itemId,
        payload: data,
      });
      const next = !!res?.data?.favorited;
      setIsFavorited(next);
      if (typeof onFavoriteChange === 'function') {
        onFavoriteChange(itemId, next, res?.data);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        try { window.dispatchEvent(new CustomEvent('auth:login-required', { detail: { from: getReturnPath() } })); } catch {}
        return;
      }
      const msg = e?.response?.data?.error || t('listings.actionFailed', '操作失败，请稍后再试');
      alert(msg);
    }
  };

  const degreeClass = (() => {
    const d = (data.degree || '').toLowerCase();
    if (d.includes('phd') || d.includes('博士')) return 'degree-phd';
    if (d.includes('本科') || d.includes('bachelor')) return 'degree-bachelor';
    if (d.includes('硕士') || d.includes('master')) return 'degree-master';
    return '';
  })();

  const school = typeof data?.school === 'string' ? data.school.trim() : '';
  const displayName = typeof data?.name === 'string' ? data.name.trim() : '';
  const timezoneLabel = formatTimezoneWithCity(data.timezone, t);
  const courses = Array.isArray(data?.courses) ? data.courses : [];
  const coursesLabel = courses
    .map((c) => {
      const label = String(c ?? '').trim();
      return label ? getCourseDirectionDisplayLabel(label, label) : '';
    })
    .filter(Boolean)
    .join(' | ');
  const languagesRaw = typeof data?.languages === 'string' ? data.languages : '';
  const ratingRaw = Number.parseFloat(String(data?.rating ?? 0));
  const ratingValue = Number.isFinite(ratingRaw) && ratingRaw > 0 ? Math.round(ratingRaw * 10) / 10 : 0;
  const reviewRaw = Number.parseInt(String(data?.reviewCount ?? 0), 10);
  const reviewCountValue = Number.isFinite(reviewRaw) && reviewRaw > 0 ? reviewRaw : 0;
  const languageTokens = languagesRaw
    ? languagesRaw.split(',').map((lang) => lang.trim()).filter(Boolean)
    : [];
  const translateLanguageToken = (lang) => {
    const raw = String(lang || '').trim();
    const normalized = raw.toLowerCase();
    if (raw === '中文' || normalized === 'zh' || normalized === 'chinese') return t('listings.language.zh', '中文');
    if (raw === '英语' || normalized === 'en' || normalized === 'english') return t('listings.language.en', '英语');
    if (raw === '日语' || normalized === 'ja' || normalized === 'japanese') return t('listings.language.ja', '日语');
    if (raw === '德语' || normalized === 'de' || normalized === 'german') return t('listings.language.de', '德语');
    if (raw === '法语' || normalized === 'fr' || normalized === 'french') return t('listings.language.fr', '法语');
    if (raw === '西班牙语' || normalized === 'es' || normalized === 'spanish') return t('listings.language.es', '西班牙语');
    return raw;
  };
  const degreeLabel = (() => {
    const raw = String(data?.degree || '').trim();
    const lower = raw.toLowerCase();
    if (lower.includes('phd') || raw.includes('博士')) return t('profile.degree.phd', raw || '博士');
    if (raw.includes('本科') || lower.includes('bachelor')) return t('profile.degree.bachelor', raw || '本科');
    if (raw.includes('硕士') || lower.includes('master')) return t('profile.degree.master', raw || '硕士');
    return raw;
  })();

  const id = typeof data?.id !== 'undefined' && data?.id !== null ? String(data.id).trim() : '';
  const profileHref = id ? `/student/mentors/${encodeURIComponent(id)}` : '';
  const avatarSeed = id || displayName || school || timezoneLabel || 'student';
  const avatarSrc = resolveAvatarSrc({
    src: data?.imageUrl,
    name: displayName,
    seed: avatarSeed,
    size: 320,
  });

  return (
    <a
      ref={revealRef}
      className={`listing-card reveal ${visible ? 'is-visible' : ''}`}
      href={profileHref || undefined}
      target={profileHref ? '_blank' : undefined}
      rel={profileHref ? 'noopener noreferrer' : undefined}
      aria-label={t('listings.openMentorProfile', '在新页面打开导师主页：{name}', { name: data?.name || data?.id || '' })}
    >
      {/* 右上角的爱心图标 */}
      <div className={`favorite-icon ${isFavorited ? 'favorited' : ''}`} onClick={toggleFavorite}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="heart-icon"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      </div>
      {/* 左上角性别标记：男=蓝色♂，女=粉色♀ */}
      {data.gender && (data.gender === '男' || data.gender === '女') && (
        <div className={`gender-badge ${data.gender === '男' ? 'gender-male' : 'gender-female'}`} aria-hidden="true">
          {data.gender === '男' ? '♂' : '♀'}
        </div>
      )}
      <img
        className="listing-avatar"
        src={avatarSrc}
        alt={data.name}
        onError={(event) => applyAvatarFallback(event, {
          name: displayName,
          seed: avatarSeed,
          size: 320,
        })}
      />
      <h3 className="listing-name">
        {data.name}{' '}
        <span className="listing-tags">
          <span className={`listing-tag ${degreeClass}`}>
            {degreeLabel}
          </span>
          {school ? <span className="listing-tag">{school}</span> : null}
        </span>
      </h3>
      <p className="listing-rating">
        <svg
          className="rating-star"
          viewBox="0 0 24 24"
          role="img"
          aria-label="rating star"
          focusable="false"
        >
          <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
        <span className="rating-text">{ratingValue} | {t('listings.reviews', '{count} 条评价', { count: reviewCountValue })}</span>
      </p>
      {/* 时区和语言合并 */}
      <div className="listing-timezone-languages">
        <span className="timezone">🌍 {timezoneLabel}</span>
        <div className="listing-languages">
          {languageTokens.map((lang, index) => (
            <span key={index} className={`language-tag ${lang.trim()}-tag`}>
              {translateLanguageToken(lang)}
            </span>
          ))}
        </div>
      </div>
      {coursesLabel ? <p className="listing-courses">{coursesLabel}</p> : null}
    </a>
  );
}

export default StudentListingCard;
